import moment from 'moment-timezone';
import { getAppNameByAppPath } from '@/utils/utility';
import { isWorkingDay, getHolidayForDate } from '@/utils/attendanceUtils';
import {
  KEY_HIGHLIGHTS_DATA,
  OVERALL_PRODUCTIVITY_DATA,
} from './data/dashboardOverviewNew.data';

// Normalize raw API response into the shape calculateStatCardData expects
export function mapTimeDataFromResponse(data) {
  return {
    totalHours: data?.totalHours ?? 0,
    totalMinutes: data?.totalMinutes ?? 0,
    idleHours: data?.idleHours ?? 0,
    idleMinutes: data?.idleMinutes ?? 0,
    productiveHours: data?.productiveHours ?? 0,
    productiveMinutes: data?.productiveMinutes ?? 0,
  };
}

export function scoreLabel(score) {
  if (score >= 80) return 'Good';
  if (score >= 60) return 'Average';
  return 'Low';
}

export const APP_TILE_COLORS = [
  '#7863fa', // purple  (largest tile)
  '#579def', // blue
  '#39b0aa', // teal
  '#e98f3b', // orange
  '#9ea7b7', // gray    (Others)
];

export function fmtSeconds(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.floor(s % 60)}s`;
}

export function buildMemberLookup(options) {
  const byId = new Map(options.map((m) => [m.value, m]));
  return {
    employeeIdForUser: (userId) =>
      userId ? (byId.get(userId)?.employeeId ?? userId) : '',
  };
}

const HOUR_LABELS = [
  '12 AM',
  '1 AM',
  '2 AM',
  '3 AM',
  '4 AM',
  '5 AM',
  '6 AM',
  '7 AM',
  '8 AM',
  '9 AM',
  '10 AM',
  '11 AM',
  '12 PM',
  '1 PM',
  '2 PM',
  '3 PM',
  '4 PM',
  '5 PM',
  '6 PM',
  '7 PM',
  '8 PM',
  '9 PM',
  '10 PM',
  '11 PM',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Distributes session time across hour buckets, returns weighted avg activityPct per slot
function distributeSessionToSlots(session, slots, slotDurationMs) {
  if (!session.start || !session.end) return;
  const sMs = new Date(session.start).getTime();
  const eMs = new Date(session.end).getTime();
  const pct = session.activityPct || 0;
  const base = new Date(session.start);
  base.setHours(0, 0, 0, 0);
  const baseMs = base.getTime();

  for (let i = 0; i < slots.length; i++) {
    const slotS = baseMs + i * slotDurationMs;
    const slotE = slotS + slotDurationMs;
    const oS = Math.max(sMs, slotS);
    const oE = Math.min(eMs, slotE);
    if (oE > oS) {
      const sec = (oE - oS) / 1000;
      slots[i].totalSec += sec;
      slots[i].weightedPct += sec * pct;
    }
  }
}

// Returns 24-point array { time, score } — weighted avg activityPct per hour across all days
export function computeHourlyProductivity(sessionsByDay) {
  const slots = Array.from({ length: 24 }, () => ({
    totalSec: 0,
    weightedPct: 0,
  }));
  if (sessionsByDay?.length) {
    for (const day of sessionsByDay) {
      for (const session of day.sessions) {
        distributeSessionToSlots(session, slots, 3600000); // 1-hour slots
      }
    }
  }
  return HOUR_LABELS.map((time, h) => ({
    time,
    score:
      slots[h].totalSec > 0
        ? Math.round(slots[h].weightedPct / slots[h].totalSec)
        : 0,
  }));
}

// Returns exactly 7 rows { day, slots[13] } — weighted avg activityPct per 2-hour window.
// Days with no session data render as all-zero (white) cells.
export function computeHeatmapData(sessionsByDay, selectedDate) {
  const anchor = selectedDate || moment().format('YYYY-MM-DD');
  const byKey = new Map(
    (sessionsByDay || []).map((d) => [d.dayKey, d.sessions || []])
  );

  return Array.from({ length: 7 }, (_, i) =>
    moment(anchor)
      .subtract(6 - i, 'days')
      .format('YYYY-MM-DD')
  ).map((dateStr) => {
    const sessions = byKey.get(dateStr) || [];
    const slots = Array.from({ length: 13 }, () => ({
      totalSec: 0,
      weightedPct: 0,
    }));
    for (const session of sessions) {
      distributeSessionToSlots(session, slots, 7200000);
    }
    const dayDate = new Date(dateStr + 'T12:00:00');
    return {
      day: DAY_NAMES[dayDate.getDay()],
      slots: slots.map((s) =>
        s.totalSec > 0 ? Math.min(1, s.weightedPct / s.totalSec / 100) : 0
      ),
    };
  });
}

// Returns all "monthname-year" keys (e.g. "june-2026") in the given date range
export function getMonthsInRange(startStr, endStr) {
  const months = [];
  const cur = moment(startStr).startOf('month');
  const end = moment(endStr).startOf('month');
  while (cur.isSameOrBefore(end, 'month')) {
    months.push(cur.format('MMMM-YYYY').toLowerCase());
    cur.add(1, 'month');
  }
  return months;
}

// Derives { value, sublabel } for the Attendance highlights card from attendanceByMonth data
export function computeAttendanceCard(rawAttendanceData, dateRange) {
  if (!rawAttendanceData) return null;
  const start = moment(dateRange.start);
  const end = moment(dateRange.end);

  if (start.isSame(end, 'day')) {
    const key = start.format('DD:MM:YYYY');
    const day = rawAttendanceData[key];
    if (!day) return { value: '—', sublabel: '' };
    if (day.leave) return { value: 'On Leave', sublabel: day.leaveType || '' };
    const sublabel = day.startTime
      ? `In: ${moment(day.startTime).format('h:mm A')}`
      : '';
    return { value: day.present ? 'Present' : 'Absent', sublabel };
  }

  let present = 0,
    total = 0;
  const cur = start.clone();
  while (cur.isSameOrBefore(end, 'day')) {
    const key = cur.format('DD:MM:YYYY');
    if (key in rawAttendanceData) {
      total++;
      const day = rawAttendanceData[key];
      if (day.present || day.leave) present++;
    }
    cur.add(1, 'day');
  }
  if (total === 0) return { value: '—', sublabel: '' };
  const pct = Math.round((present / total) * 100);
  return { value: `${present}/${total}`, sublabel: `${pct}% attendance` };
}

export const LIVE_HIGHLIGHT_IDS = new Set([
  'active-time',
  'idle-time',
  'focus-time',
  'alerts',
  'attendance',
]);

// ── Firestore-backed builders for user dashboard ──────────────────────────

function pctDelta(current, previous) {
  if (!previous || previous === 0) return { pct: 0, up: true };
  const delta = ((current - previous) / previous) * 100;
  return { pct: Math.abs(Math.round(delta)), up: delta >= 0 };
}

export function buildUserOverallProductivity(today, yesterday, teamDay) {
  const score = today ? Math.round(today.productivityPercentage || 0) : 0;
  const delta = pctDelta(
    today?.productivityPercentage,
    yesterday?.productivityPercentage
  );
  const teamAvg = teamDay
    ? Math.round(teamDay.productivityPercentage || 0)
    : score;
  return {
    ...OVERALL_PRODUCTIVITY_DATA,
    score,
    label: scoreLabel(score),
    trendPct: delta.pct,
    trendUp: delta.up,
    teamAvg,
  };
}

export function buildUserKeyHighlights(today, yesterday, rawAlerts, focusData) {
  const totalSec = today?.totalSec || 0;
  const idleSec = today?.idleSec || 0;
  const productiveSec = today?.productiveSec || 0;

  const activeTimeDelta = pctDelta(productiveSec, yesterday?.productiveSec);
  const idleTimeDelta = pctDelta(idleSec, yesterday?.idleSec);
  const alertsDelta = pctDelta(today?.alertsCount, yesterday?.alertsCount);

  const rawActivePct = totalSec > 0 ? (productiveSec / totalSec) * 100 : 0;
  const rawIdlePct = totalSec > 0 ? (idleSec / totalSec) * 100 : 0;
  const scale =
    rawActivePct + rawIdlePct > 100 ? 100 / (rawActivePct + rawIdlePct) : 1;
  const activePct = Math.round(rawActivePct * scale);
  const idlePct = Math.round(rawIdlePct * scale);

  return KEY_HIGHLIGHTS_DATA.map((item) => {
    switch (item.id) {
      case 'active-time':
        return {
          ...item,
          value: productiveSec > 0 ? fmtSeconds(productiveSec) : '—',
          trendPct: activeTimeDelta.pct,
          trendUp: activeTimeDelta.up,
          sublabel: totalSec > 0 ? `${activePct}%` : '—',
          sublabelSuffix: 'of tracked time',
        };
      case 'idle-time':
        return {
          ...item,
          value: idleSec > 0 ? fmtSeconds(idleSec) : '—',
          trendPct: idleTimeDelta.pct,
          trendUp: idleTimeDelta.up,
          sublabel: totalSec > 0 ? `${idlePct}%` : '—',
          sublabelSuffix: 'of tracked time',
        };
      case 'attendance': {
        if (!today)
          return { ...item, value: '—', sublabel: '', sublabelSuffix: '' };
        if (!today.present && !today.firstLoginAt) {
          return { ...item, value: 'Absent', sublabel: '', sublabelSuffix: '' };
        }
        const checkIn = today.firstLoginAt
          ? `In: ${moment(
              today.firstLoginAt.toDate
                ? today.firstLoginAt.toDate()
                : today.firstLoginAt
            ).format('h:mm A')}`
          : '';
        return {
          ...item,
          value: today.isLate ? 'Late' : 'Present',
          sublabel: checkIn,
          sublabelSuffix: '',
          trendPct: 0,
          trendUp: true,
        };
      }
      case 'focus-time':
        return {
          ...item,
          value: focusData ? focusData.segments[0].time : '—',
          trendPct: 0,
          trendUp: true,
          sublabel: focusData ? `${focusData.focusPct}%` : '—',
          sublabelSuffix: focusData ? 'of tracked time' : '',
        };
      case 'alerts':
        return {
          ...item,
          value: today?.alertsCount != null ? String(today.alertsCount) : '—',
          trendPct: alertsDelta.pct,
          trendUp: alertsDelta.up,
        };
      default:
        return item;
    }
  });
}

export function buildUserProductivityOverTime(today, teamDay, timezone) {
  if (!today?.hourlyTotalSec) return [];
  const tz = timezone || moment.tz.guess();
  const { hourlyTotalSec = {}, hourlyProductiveSec = {}, dateId = '' } = today;
  const teamHourlyTotal = teamDay?.hourlyTotalSec || {};
  const teamHourlyProductive = teamDay?.hourlyProductiveSec || {};

  return Array.from({ length: 24 }, (_, utcH) => {
    const totalSec = Number(hourlyTotalSec[utcH]) || 0;
    const productiveSec = Number(hourlyProductiveSec[utcH]) || 0;
    const score =
      totalSec > 0 ? Math.round((productiveSec / totalSec) * 100) : 0;

    const tTeamTotal = Number(teamHourlyTotal[utcH]) || 0;
    const tTeamProductive = Number(teamHourlyProductive[utcH]) || 0;
    const teamAvg =
      tTeamTotal > 0 ? Math.round((tTeamProductive / tTeamTotal) * 100) : 0;

    const label = moment
      .utc(`${dateId}T${String(utcH).padStart(2, '0')}:00:00Z`)
      .tz(tz)
      .format('h A');
    return { time: label, score, teamAvg };
  });
}

export function buildUserChartTicks(today, timezone) {
  const data = buildUserProductivityOverTime(today, null, timezone);
  if (!data.length) return undefined;
  return data.filter((_, i) => i % 4 === 0).map((d) => d.time);
}

export function buildUserActivityBreakdown(today) {
  if (!today?.totalSec) return null;
  const {
    totalSec,
    productiveSec = 0,
    idleSec = 0,
    firstLoginAt,
    lastPingAt,
  } = today;

  let offlineSec = 0;
  if (firstLoginAt && lastPingAt) {
    const firstMs =
      typeof firstLoginAt.toMillis === 'function'
        ? firstLoginAt.toMillis()
        : new Date(firstLoginAt).getTime();
    const lastMs =
      typeof lastPingAt.toMillis === 'function'
        ? lastPingAt.toMillis()
        : new Date(lastPingAt).getTime();
    offlineSec = Math.max(0, (lastMs - firstMs) / 1000 - totalSec);
  }

  const spanSec = totalSec + offlineSec;
  const activePct = Math.round((productiveSec / spanSec) * 100);
  const idlePct = Math.round((idleSec / spanSec) * 100);
  const offlinePct = Math.max(0, 100 - activePct - idlePct);

  return {
    totalTime: fmtSeconds(productiveSec),
    label: 'Total Active Time',
    segments: [
      { name: 'Active', value: activePct, color: '#22c55e' },
      { name: 'Idle', value: idlePct, color: '#f59e0b' },
      { name: 'Offline', value: offlinePct, color: '#9CA3AF' },
    ],
  };
}

export function buildUserTopApplications(appUsage, appNameMapping) {
  if (!appUsage || !Object.keys(appUsage).length) return null;
  const totalSeconds = Object.values(appUsage).reduce((s, v) => s + v, 0);
  if (totalSeconds === 0) return null;

  const sorted = Object.entries(appUsage).sort(([, a], [, b]) => b - a);
  const top = sorted.slice(0, 4).map(([path, secs], i) => ({
    name: getAppNameByAppPath(path, appNameMapping),
    time: fmtSeconds(secs),
    pct: ((secs / totalSeconds) * 100).toFixed(2),
    color: APP_TILE_COLORS[i],
  }));

  const restSec = sorted.slice(4).reduce((s, [, v]) => s + v, 0);
  if (restSec > 0) {
    top.push({
      name: 'Others',
      time: fmtSeconds(restSec),
      pct: ((restSec / totalSeconds) * 100).toFixed(2),
      color: APP_TILE_COLORS[4],
    });
  }

  return top;
}

// Builds the selected member's attendance summary for the month containing
// dateRange.start, from their attendanceByMonth doc. Only present/leave are
// derivable from that doc (it never records isLate), so no Late segment —
// any day that's neither present nor leave counts as absent. Non-working
// days (per the company's workingSchedule) and company holidays are skipped
// entirely so they don't dilute the percentage.
export function buildUserAttendanceSummary(
  monthAttendanceData,
  dateRange,
  workingDays,
  holidays
) {
  if (!monthAttendanceData || !dateRange?.start) return null;

  const monthStart = moment(dateRange.start).startOf('month');
  const today = moment().startOf('day');
  const monthEnd = moment.min(moment(dateRange.start).endOf('month'), today);
  if (monthEnd.isBefore(monthStart, 'day')) return null;

  let presentCount = 0;
  let leaveCount = 0;
  let totalCount = 0;
  const cur = monthStart.clone();
  while (cur.isSameOrBefore(monthEnd, 'day')) {
    if (!isWorkingDay(cur, workingDays) || getHolidayForDate(cur, holidays)) {
      cur.add(1, 'day');
      continue;
    }
    totalCount++;
    const day = monthAttendanceData[cur.format('DD:MM:YYYY')];
    if (day?.present) presentCount++;
    else if (day?.leave) leaveCount++;
    cur.add(1, 'day');
  }
  if (totalCount === 0) return null;

  const absentCount = totalCount - presentCount - leaveCount;
  return {
    overall: Math.round((presentCount / totalCount) * 100),
    presentCount,
    totalCount,
    segments: [
      { name: 'Present', value: presentCount, color: '#22c55e' },
      { name: 'Leave', value: leaveCount, color: '#f59e0b' },
      { name: 'Absent', value: absentCount, color: '#ef4444' },
    ],
  };
}

export function buildUserActivityHeatmap(last7, selectedDate, timezone) {
  const tz = timezone || moment.tz.guess();
  const docByDate = new Map((last7 || []).map((d) => [d.dateId, d]));

  const dates = Array.from({ length: 7 }, (_, i) =>
    moment(selectedDate)
      .subtract(6 - i, 'days')
      .format('YYYY-MM-DD')
  );

  const rawRows = dates.map((dateId) => {
    const hourlyTotalSec = docByDate.get(dateId)?.hourlyTotalSec || {};
    const slots = Array(24).fill(0);

    for (const [utcHStr, sec] of Object.entries(hourlyTotalSec)) {
      const localH = moment
        .utc(`${dateId}T${String(Number(utcHStr)).padStart(2, '0')}:00:00Z`)
        .tz(tz)
        .hour();
      slots[localH] += Number(sec) || 0;
    }

    return { day: moment(dateId).format('ddd'), slots };
  });

  const maxSec = Math.max(...rawRows.flatMap((r) => r.slots), 1);

  return rawRows.map(({ day, slots }) => ({
    day,
    slots: slots.map((s) => s / maxSec),
  }));
}
